import VueCookies from 'vue-cookies';
import axios from 'axios';

import * as Sentry from '@sentry/vue';
import commonHelper, { isBlank, warn } from 'adready-api/helpers/common';
import accountApi from 'adready-api/api/account';
import { COOKIE_NAME_REDIRECT_URI } from 'adready-api/helpers/cookie-session-helper';
import moment from 'moment';
import config from '~/config';

import forklift from '~/components/mixins/forklift-mixin';
import store from '~/store';
import {
  KEY_ACCOUNT_ID,
  KEY_ADVERTISER_ID,
  APP_FLIP_ID,
  APP_CONSOLE_ID,
  DEMO_APP_CONSOLE_ID,
  APP_ADREADY_ID,
  KEY_DEMO_ACCOUNT_ID,
  KEY_DEMO_ADVERTISER_ID,
  RANGE_CUSTOM,
  RANGE_LAST_30_DAYS,
  COMPARE_RANGE_PREV_DAY,
  COMPARE_RANGE_CUSTOM,
  VIA_MEDIA_CLIENT_NAME_FOR_API,
  VIA_ACCOUNT_ID,
  AI_ASSISTANT_FULL_PLATFORM,
} from '~/constant';
import { isDemoInstance, filterAccountsAndAdvertisersForDemo } from '~/util/utility-functions';
import layoutHelpers from './layout-helpers';
import { initTimer, redirectToLogin } from './refresh-token';
import { clearCacheFilters } from './url-helpers';

const NEXT_URL = 'nextUrl';

// var to ensure that we run globalStartupGuard once and only once
let init;

// caches the jwt status
let hasToken = false;

/**
 * Get a valid account ID which the user has access to. If the user does not
 * have access to the given account ID, returns the first one from their list of
 * roles.
 *
 * @param {Array<UserAccount>} userAccounts
 * @param {Number} currentAccountId to test access for
 *
 * @returns {Account} valid account
 */
function getValidAccount(userAccounts, currentAccountId) {
  let currentAccount;
  if (currentAccountId) {
    // try to find the current account to ensure we have access to it
    if (userAccounts) {
      currentAccount = userAccounts.find((userAccount) => userAccount.id === currentAccountId);
      if (currentAccount && currentAccount.id === VIA_ACCOUNT_ID) {
        currentAccount.name = VIA_MEDIA_CLIENT_NAME_FOR_API;
      }
      if (!currentAccount) {
        [currentAccount] = userAccounts;
      }
      return currentAccount;
    }
  }

  if (userAccounts && userAccounts.length > 0) {
    // was invalid or not passed in, default to first accountId we have access to
    const sortedList = commonHelper.caseInsensitiveSort([].concat(userAccounts), 'name');
    // eslint-disable-next-line prefer-destructuring
    currentAccount = sortedList[0];
    if (currentAccount && currentAccount.id === VIA_ACCOUNT_ID) {
      currentAccount.name = VIA_MEDIA_CLIENT_NAME_FOR_API;
    }
  }

  return currentAccount;
}

/**
 * Get a valid advertiser ID which the user has access to. If the user does not
 * have access to the given advertiser ID, returns the first one from their list of
 * roles.
 *
 * @param {Account} currentAccount
 * @param {Number} currentAdvertiserId to test access for
 *
 * @returns {Advertiser} valid advertiser
 */
function getValidAdvertiser(currentAccount, currentAdvertiserId) {
  if (!Number.isNaN(currentAdvertiserId)) {
    // try to find the current account to ensure we have access to it
    if (currentAccount) {
      let ua = currentAccount.advertisers.find((adv) => adv.id === currentAdvertiserId);
      if (!ua) {
        const sortedList = commonHelper.caseInsensitiveSort(
          [].concat(currentAccount.advertisers),
          'name'
        );
        [ua] = sortedList;
      }
      return ua;
    }
  }

  if (currentAccount && currentAccount.advertisers && currentAccount.advertisers.length > 0) {
    // was invalid or not passed in, default to first advertiserId we have access to
    const sortedList = commonHelper.caseInsensitiveSort(
      [].concat(currentAccount.advertisers),
      'name'
    );
    return sortedList[0];
  }

  return undefined;
}

function fetchUserAccount(currentUserId, currentAccountId, currentAdvertiserId) {
  const key = 'common/userAccounts';
  const userAccounts = store.get(key);
  let retAccount = null;
  let retAdvertiser = null;
  const currentAccount = getValidAccount(userAccounts, currentAccountId);
  if (currentAccount) {
    retAccount = currentAccount;
    store.set('common/currentAccountId', currentAccount.id);
    localStorage.setItem(KEY_ACCOUNT_ID, currentAccount.id);
    store.set('common/account', currentAccount);

    const currentAdvertiser = getValidAdvertiser(currentAccount, currentAdvertiserId);
    if (currentAdvertiser && currentAdvertiser.id) {
      retAdvertiser = currentAdvertiser;
      localStorage.setItem(KEY_ADVERTISER_ID, currentAdvertiser.id);
      store.set('common/advertiser', currentAdvertiser);
    }
  }
  return { account: retAccount, advertiser: retAdvertiser };
}

async function fetchAsyncUserAccount(currentUserId, currentAccountId, currentAdvertiserId) {
  const promises = [];
  promises.push(accountApi.userAccount(currentUserId));
  return Promise.all(promises).then((responses) => {
    const demoAdvertiserMappings = store.get('common/demoAdvertiserMappings');
    const accountKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.accountId, 10));
    const advertiserKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.advertiserId, 10));

    const filteredUserAccounts = filterAccountsAndAdvertisersForDemo(
      responses[0],
      accountKeys,
      advertiserKeys
    );
    let retAccount = null;
    let retAdvertiser = null;
    const currentAccount = getValidAccount(filteredUserAccounts, currentAccountId);
    if (currentAccount) {
      retAccount = currentAccount;
      store.set('common/currentAccountId', currentAccount.id);
      localStorage.setItem(KEY_ACCOUNT_ID, currentAccount.id);
      store.set('common/account', currentAccount);

      const currentAdvertiser = getValidAdvertiser(currentAccount, currentAdvertiserId);
      if (currentAdvertiser && currentAdvertiser.id) {
        retAdvertiser = currentAdvertiser;
        localStorage.setItem(KEY_ADVERTISER_ID, currentAdvertiser.id);
        store.set('common/advertiser', currentAdvertiser);
      }
    }
    return { account: retAccount, advertiser: retAdvertiser };
  });
}

async function fetchDemoAccount(currentUserId, currentAccountId, currentAdvertiserId) {
  if (Number.isNaN(currentAccountId) || Number.isNaN(currentAdvertiserId)) {
    const ua = await fetchAsyncUserAccount(currentUserId, currentAccountId, currentAdvertiserId);
    currentAccountId = ua?.account?.id;
    currentAdvertiserId = ua?.advertiser?.id;
  }
  if (!currentAccountId || !currentAdvertiserId) {
    return { account: null, advertiser: null };
  }

  const demoAdvertisers = store.get('common/GET_DEMO_ADVERTISER_MAPPING_MAP');
  const keys = Object.keys(demoAdvertisers).map((key) => {
    return parseInt(key, 10);
  });

  const demoAdvertiserMappingArr = keys.includes(currentAdvertiserId)
    ? demoAdvertisers[currentAdvertiserId]
    : demoAdvertisers[1691];

  const demoAdvertiserMapping =
    Array.isArray(demoAdvertiserMappingArr) && demoAdvertiserMappingArr.length > 0
      ? demoAdvertiserMappingArr[0]
      : undefined;

  const dates = store.get('dashboard/dates');

  const updatedDates = {
    ...dates,
    startDate:
      demoAdvertiserMapping.startDate && demoAdvertiserMapping.endDate
        ? moment(demoAdvertiserMapping.startDate)
        : moment(new Date().setDate(new Date().getDate() - 31)),
    endDate:
      demoAdvertiserMapping.startDate && demoAdvertiserMapping.endDate
        ? moment(demoAdvertiserMapping.endDate)
        : moment(new Date().setDate(new Date().getDate() - 2)),
    compareStartDate:
      demoAdvertiserMapping.compareStartDate && demoAdvertiserMapping.compareEndDate
        ? moment(demoAdvertiserMapping.compareStartDate)
        : moment(new Date().setDate(new Date().getDate() - 32)),
    compareEndDate:
      demoAdvertiserMapping.compareStartDate && demoAdvertiserMapping.compareEndDate
        ? moment(demoAdvertiserMapping.compareEndDate)
        : moment(new Date().setDate(new Date().getDate() - 3)),
    dateRangeOption:
      demoAdvertiserMapping.startDate || demoAdvertiserMapping.endDate
        ? RANGE_CUSTOM
        : RANGE_LAST_30_DAYS,
    dateCompareOption:
      demoAdvertiserMapping.compareStartDate && demoAdvertiserMapping.compareEndDate
        ? COMPARE_RANGE_CUSTOM
        : COMPARE_RANGE_PREV_DAY,
  };
  store.set('dashboard/dates', updatedDates);

  const mappedAccountId = parseInt(currentAccountId, 10);
  const promises = [];

  // Add error handling for `accountApi.account(mappedAccountId)` call
  promises.push(accountApi.userAccount(currentUserId));

  /**
   * when user logs in as a  demo user there is mapping account associated with the demo account
   * if mapped account is not assigned to the the user server will throw unauthorised access error
   * for mapped account since not assigned to the user yet. this will make incomplete session load
   * causing globalStartupGuard to reload infinite
   * to stop infinite reload due to incomplete session data, setting up dummy values to mock the empty session.
   *
   */

  promises.push(
    accountApi.account(mappedAccountId).catch((error) => {
      console.error('Error fetching account:', error);
      // Handle the error and stop the infinite reload by preventing further execution
      store.set('common/currentAccountId', 0);
      localStorage.setItem(KEY_ACCOUNT_ID, 0);
      localStorage.setItem(KEY_ADVERTISER_ID, 0);
      localStorage.setItem(KEY_DEMO_ACCOUNT_ID, 0);
      localStorage.setItem(KEY_DEMO_ADVERTISER_ID, 0);
      store.set('common/account', 0);
      store.set('common/advertiser', 0);
      return Promise.reject(new Error('Failed to fetch account. Please try again.'));
    })
  );

  return Promise.all(promises)
    .then((responses) => {
      let retAccount = null;
      let retAdvertiser = null;
      const demoAdvertiserMappings = store.get('common/demoAdvertiserMappings');
      const accountKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.accountId, 10));
      const advertiserKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.advertiserId, 10));
      const userAccounts = filterAccountsAndAdvertisersForDemo(
        responses[0],
        accountKeys,
        advertiserKeys
      );
      const mappedAccount = responses[1];
      const currentAccount = getValidAccount(userAccounts, currentAccountId);
      if (currentAccount && mappedAccount) {
        const currentAdvertiser = getValidAdvertiser(currentAccount, currentAdvertiserId);
        localStorage.setItem(KEY_DEMO_ACCOUNT_ID, currentAccount.id);
        localStorage.setItem(KEY_DEMO_ADVERTISER_ID, currentAdvertiser?.id);
        store.set('common/demoSelectedAdvertiserName', currentAdvertiser?.name);

        const {
          logoFile,
          appConsoleLogoFile,
          appConsoleSquareLogoFile,
          whitelabelEnabled,
          theme,
        } = currentAccount.organization;

        mappedAccount.organization.logoFile = JSON.parse(JSON.stringify(logoFile));
        mappedAccount.organization.appConsoleLogoFile = JSON.parse(
          JSON.stringify(appConsoleLogoFile)
        );
        mappedAccount.organization.appConsoleSquareLogoFile = JSON.parse(
          JSON.stringify(appConsoleSquareLogoFile)
        );
        mappedAccount.organization.whitelabelEnabled = whitelabelEnabled;
        mappedAccount.organization.theme = JSON.parse(JSON.stringify(theme));
        mappedAccount.ziplyneAccount = currentAccount?.name;

        retAccount = mappedAccount;
        const mappedAdvertiser = getValidAdvertiser(
          mappedAccount,
          demoAdvertiserMapping.advertiserId
        );
        mappedAdvertiser.ziplyneAdvertiser = currentAdvertiser?.name;
        retAdvertiser = mappedAdvertiser;
        store.set('common/currentAccountId', mappedAccount.id);
        localStorage.setItem(KEY_ACCOUNT_ID, mappedAccount.id);
        localStorage.setItem(KEY_ADVERTISER_ID, mappedAdvertiser.id);
        if (mappedAccount.id === VIA_ACCOUNT_ID) {
          mappedAccount.name = VIA_MEDIA_CLIENT_NAME_FOR_API;
        }
        store.set('common/account', mappedAccount);
        store.set('common/advertiser', mappedAdvertiser);
      }
      return { account: retAccount, advertiser: retAdvertiser };
    })
    .catch((error) => {
      console.error('Error during fetchDemoAccount:', error);
      return { account: null, advertiser: null }; // Return null objects to stop infinite reload
    });
}

function setCurrentAccount(currentUserId, accountId) {
  // prefer the accountId that was passed in, if not valid, use value stored in localStorage
  // accountId = 77;
  let currentAccountId = accountId;
  let currentAdvertiserId = 0;
  if (!(currentAccountId && !Number.isNaN(currentAccountId))) {
    const accountKeyName = isDemoInstance() ? KEY_DEMO_ACCOUNT_ID : KEY_ACCOUNT_ID;
    currentAccountId = parseInt(localStorage.getItem(accountKeyName), 10);
    const advertiserKeyName = isDemoInstance() ? KEY_DEMO_ADVERTISER_ID : KEY_ADVERTISER_ID;
    currentAdvertiserId = parseInt(localStorage.getItem(advertiserKeyName), 10);
  }

  if (isDemoInstance()) {
    return fetchDemoAccount(currentUserId, currentAccountId, currentAdvertiserId);
  }
  return fetchUserAccount(currentUserId, currentAccountId, currentAdvertiserId);
}

/**
 * applyTheme is applicable only to non-login screens i.e. auth pages
 *
 */
const applyTheme = async () => {
  const configThemeUrl = `${config.CONFIG_API_URL}/api/theme?domain=${window.location.hostname}`;
  let configTheme = '';
  await axios.get(configThemeUrl).then((val) => {
    configTheme = val.data;
  });
  if (configTheme.primaryColor) {
    document.documentElement.style.setProperty('--primaryColor', config.PRIMARY_COLOR);
  }

  if (configTheme.overtext) {
    document.documentElement.style.setProperty('--overtext', config.BUTTON_TEXT);
  }
  const style = document.createElement('style');
  style.textContent = configTheme;
  document.head.appendChild(style);
};

function clearLocalStorageFiltersOnSessionTimeout() {
  // Get the saved path from local storage.
  const savedPathString = localStorage.getItem('selectedPath');
  if (savedPathString && savedPathString.length > 0) {
    try {
      const savedPath = JSON.parse(savedPathString);
      // Remove the childOption to land on first solution
      delete savedPath.childOption;
      // save it back to localStorage.
      localStorage.setItem('selectedPath', JSON.stringify(savedPath));
    } catch (error) {
      console.error('Failed to parse or modify savedPath from localStorage:', error);
    }
  }
  // clear filters in storage
  localStorage.removeItem('initialReportBuilderFilters');
  localStorage.removeItem('filters_cache_filterCacheId');
  localStorage.removeItem('initialFilters');
  sessionStorage.removeItem('selectedDates');
}

/**
 * Load user session from JWT
 *
 * Decodes the JWT to extract:
 * - User ID (subject aka sub)
 *
 * @returns {Boolean} true if successful (user has valid session)
 */
export async function loadSession() {
  let decoded;
  try {
    decoded = await axios.get(`${config.ADREADY_URL}/api/token/parse`, { withCredentials: true });
    decoded = decoded?.data?.result;
    VueCookies.remove(COOKIE_NAME_REDIRECT_URI);
    initTimer(decoded.expires);
  } catch (err) {
    warn('error decoding token:', err);
    clearLocalStorageFiltersOnSessionTimeout();
    await Promise.allSettled([
      applyTheme(),
      axios.delete(`${config.ADREADY_URL}/api/token/remove`, { withCredentials: true }),
    ]);
    return false;
  }

  if (!decoded) {
    return false;
  }

  const allowedAppsAccess = decoded.apps;
  if (!allowedAppsAccess.includes(APP_CONSOLE_ID)) {
    console.warn('Not Allowed to Access AppConsole');
    if (!isBlank(config.FLIP_URL) && allowedAppsAccess.includes(APP_FLIP_ID)) {
      window.location = config.FLIP_URL;
    }
    if (!isBlank(config.ADREADY_URL) && allowedAppsAccess.includes(APP_ADREADY_ID)) {
      window.location = config.ADREADY_URL;
    }
  }

  // Check if the user has access to the required app
  const requiredAppId = isDemoInstance() ? DEMO_APP_CONSOLE_ID : APP_CONSOLE_ID;
  if (!allowedAppsAccess.includes(requiredAppId)) {
    // prevent session creation, trigger logout
    await Promise.allSettled([
      applyTheme(),
      axios.delete(`${config.ADREADY_URL}/api/token/remove`, { withCredentials: true }),
    ]);
    return false;
  }

  // read user ID from the token
  const currentUserId = decoded.userId;
  store.set('common/currentUserId', currentUserId);
  Sentry.setUser({ id: currentUserId.toString() });

  const loggedUser = localStorage.getItem('loggedUserId');
  if (loggedUser !== null && loggedUser !== currentUserId.toString()) {
    clearCacheFilters('login');
  }
  localStorage.setItem('loggedUserId', currentUserId);

  let user = null;
  try {
    const [userResult] = await Promise.allSettled([
      forklift.methods.loadCurrentUser(currentUserId, true),
      forklift.methods.loadCurrentUserRoles(currentUserId, true),
      forklift.methods.fetchDemoAdvertiserMappings(),
      forklift.methods.loadUserAccounts(currentUserId),
    ]);

    if (userResult.status === 'fulfilled') {
      user = userResult.value;
    } else {
      console.error('Could not load the current user:', userResult.reason);
      throw new Error('Failed to load essential user data.');
    }
  } catch (error) {
    console.error('A critical error occurred during the session creation process:', error);
  }

  // Inject scripts and update Sentry with user email
  forklift.methods.injectAssistantScripts(user);
  const email = user?.email?.toLowerCase() || '';
  Sentry.setUser({ id: currentUserId.toString(), email });

  if (user?.userApplications?.some((app) => app.applicationId === AI_ASSISTANT_FULL_PLATFORM)) {
    setTimeout(() => forklift.methods.loadCurrentUser(currentUserId, true), 100);
  }

  // Set the current account and advertiser
  let { account, advertiser } = await setCurrentAccount(currentUserId, null);

  // check demo mapping
  const demoAdvMappings = store.get('common/demoAdvertiserMappings');
  const demoAcctKeys = demoAdvMappings.map((obj) => parseInt(obj.accountId, 10));
  const demoAdvKeys = demoAdvMappings.map((obj) => parseInt(obj.advertiserId, 10));
  const accountKeysSet = new Set(demoAcctKeys);
  const advertiserKeysSet = new Set(demoAdvKeys);
  // if not demo instance, check if the account and the advertiser is in demo mapping,
  // if it does, set to null
  if (
    !isDemoInstance() &&
    accountKeysSet.has(account?.id) &&
    advertiserKeysSet.has(advertiser?.id)
  ) {
    account = null;
    advertiser = null;
  }

  if (!account && !advertiser) {
    store.set('common/noAdvertiserAssined', true); // for showing no adv assigned msg to the user.
    store.set('common/leftNavLoading', false);
  }

  const theUser = store.get('common/currentUser');
  const theUserId = theUser && theUser.id ? theUser.id : '';
  const theEmail = theUser && theUser.email ? theUser.email.toLowerCase() : '';
  const userOrgId = theUser && theUser.organizationId ? theUser.organizationId : '';
  const userName = theUser && theUser.userName ? theUser.userName : '';

  // Initialize pendo with logged in user details
  config?.initializePendo?.(theUserId, theEmail, userOrgId);

  // Initialize ziplyne with logged in user details
  const login = localStorage.getItem('login');
  if (login) {
    localStorage.removeItem('login');
    const key = 'common/userAccounts';
    const userAccounts = store.get(key);
    let allAdvertisers = '';
    if (isDemoInstance()) {
      accountApi.userAccount(theUserId).then((res) => {
        const demoAdvertiserMappings = store.get('common/demoAdvertiserMappings');
        const accountKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.accountId, 10));
        const advertiserKeys = demoAdvertiserMappings.map((obj) => parseInt(obj.advertiserId, 10));
        const demoAccounts = filterAccountsAndAdvertisersForDemo(res, accountKeys, advertiserKeys);
        allAdvertisers = demoAccounts.map(({ name, advertisers }) => ({
          name,
          advertisers: advertisers.map((adv) => adv.name),
        }));
        config?.ziplyneLogin?.(
          theEmail,
          userName,
          '',
          advertiser.ziplyneAdvertiser,
          allAdvertisers,
          account.ziplyneAccount
        );
      });
    } else {
      allAdvertisers = userAccounts.map(({ name, advertisers }) => ({
        name,
        advertisers: advertisers.map((adv) => adv.name),
      }));
      config?.ziplyneLogin?.(
        theEmail,
        userName,
        '',
        advertiser?.name,
        allAdvertisers,
        account?.name
      );
    }
  }

  const p1 = forklift.methods.loadFlipPixels(advertiser);
  const p2 = forklift.methods.loadAdvertiserSolutions(true, advertiser);
  const [, solutionsResult] = await Promise.allSettled([p1, p2]);
  if (solutionsResult.status === 'fulfilled') {
    const solutions = solutionsResult.value;
    const activeSolutions = solutions?.filter((s) => s.state === 'ACTIVE') || [];
    const firstSolution = activeSolutions?.[0];

    if (firstSolution) {
      store.set('common/leftNavLoading', false);
    }
  } else {
    store.set('common/leftNavLoading', false);
  }

  const changeAdvertiser = localStorage.getItem('ChangeAdvertiser');
  if (changeAdvertiser) {
    localStorage.removeItem('ChangeAdvertiser');
    // remove previous stored filters
    localStorage.removeItem('filters_cache_filterCacheId');
    localStorage.removeItem('initialFilters');
    localStorage.removeItem('selectedPath');
    config?.ziplyneChangeAdvertiser(theEmail, advertiser.name, account.name);
  }

  return true;
}

/**
 * Router guard which validates that the user is properly authenticated to
 * access the given 'to' route.
 *
 */
export function authGuard(to, from, next) {
  if (to.path === '/openid_login') {
    // Initialize pendo with anonymous user
    config?.initializeAnonymousPendo?.();
    next();
  } else if (to.meta.guest === true) {
    // Initialize pendo with anonymous user
    config?.initializeAnonymousPendo?.();
    next();
  } else if (!hasToken) {
    // Initialize pendo with anonymous user
    config?.initializeAnonymousPendo?.();
    // everything else requires user to be logged in
    localStorage.setItem(NEXT_URL, to.fullPath);
    // next({ name: 'Login' });
    const { mode } = to.query;
    redirectToLogin(mode);
  } else {
    // allow authenticated user to continue
    const nextUrl = localStorage.getItem(NEXT_URL);
    if (!isBlank(nextUrl)) {
      // have a stored redirect url, use it
      localStorage.removeItem(NEXT_URL);
      next(nextUrl);
    } else {
      // just continue on to the requested route.
      // this is the most common scenario for a logged-in user.
      next();
    }
  }
}

export async function checkQueryParameterForEmbeded(to) {
  const isEmbedMode = to.query.embed === 'true';
  // save to vuex store
  store.set('common/isEmbedMode', isEmbedMode);

  const newAccountId = parseInt(to.query.accountId, 10);
  const newAdvertiserId = parseInt(to.query.advertiserId, 10);
  // check if isEmbedMode, if is number and if either has changed
  const lastAdvertiserId = localStorage.getItem('lastAdvertiserId');
  const lastAccountId = localStorage.getItem('lastAccountId');
  if (
    isEmbedMode &&
    ((newAdvertiserId && newAdvertiserId !== parseInt(lastAdvertiserId, 10)) ||
      (newAccountId && newAccountId !== parseInt(lastAccountId, 10)))
  ) {
    localStorage.setItem('lastAdvertiserId', newAdvertiserId);
    localStorage.setItem('lastAccountId', newAccountId);

    // save new advertiser
    localStorage.setItem(KEY_ACCOUNT_ID, newAccountId);
    localStorage.setItem(KEY_ADVERTISER_ID, newAdvertiserId);
    localStorage.setItem('ChangeAdvertiser', true);

    // remove previous stored filters
    localStorage.removeItem('filters_cache_filterCacheId');
    localStorage.removeItem('initialFilters');
    localStorage.removeItem('selectedPath');

    window.location.reload();
  }
}

/**
 * Global router guard which runs exactly *once*, when the application is first
 * loaded. That is, when processing the very first route. Anything which needs
 * to run at the earliest possible time, before loading any Vue components
 * should happen here.
 *
 * Note, however, that by this point, the route is already selected, however it
 * can still be modified (by passing the next func from the guard method below).
 *
 * Currently it:
 * - loads theme
 * - loads layout
 * - handles the AuthN check for secure routes (reading the JWT)
 * - attempts to bust caches on version changes
 */
export async function globalStartupGuard(to) {
  // check if the embed query parameter is present
  await checkQueryParameterForEmbeded(to);

  if (to.path === '/openid_login') {
    await Promise.allSettled([
      applyTheme(),
      axios.delete(`${config.ADREADY_URL}/api/token/remove`, { withCredentials: true }),
    ]);
  }

  if (hasToken === false) {
    // always reload token first, when not logged in
    hasToken = await loadSession(parseInt(to.query.accountId, 10));
  }

  window.document.title = config.APP_NAME || 'Digital Remedy';

  if (init !== undefined) {
    return init;
  }

  // load theme
  layoutHelpers.updateTheme();

  if (!hasToken) {
    init = false;
    return false;
  }

  return true;
}
